/**
 * Pure helpers for the wizard's Step 6 (D1.5.7) first-owner form. Mirrors
 * the persisted `owner` block in the tenant-kit shape (design §9):
 *
 *   { email, firstName, lastName, phone?, inviteMode, tempPassword? }
 *
 * Draft values are stored as raw strings; Zod parsing happens at commit
 * time (D1.6.1) when the full kit is composed. The form's job is to make
 * invalid drafts visible inline before the admin advances.
 */

export const INVITE_MODES = ['magic_link', 'temp_password'] as const;
export type InviteMode = (typeof INVITE_MODES)[number];
export const DEFAULT_INVITE_MODE: InviteMode = 'magic_link';

// RFC 5322 lite — same shape Zod uses internally for `.email()`.
const EMAIL_PATTERN =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

// Loose phone gate — the wizard collects raw user input; per design §7 phone
// is optional. We accept E.164-ish or US-formatted strings and let the
// commit service do the strict normalization.
const PHONE_PATTERN = /^[+()\d][\d\s().+-]{6,}$/;

export const TEMP_PASSWORD_MIN_LENGTH = 12;

// ─── Email ───────────────────────────────────────────────────────────────────

export type EmailValidationError = 'empty' | 'no_at' | 'malformed';

export type EmailValidation =
  | { ok: true; value: string }
  | { ok: false; reason: EmailValidationError };

export function validateOwnerEmail(raw: string): EmailValidation {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, reason: 'empty' };
  if (!trimmed.includes('@')) return { ok: false, reason: 'no_at' };
  if (!EMAIL_PATTERN.test(trimmed)) return { ok: false, reason: 'malformed' };
  return { ok: true, value: trimmed };
}

export function describeEmailError(reason: EmailValidationError): string {
  switch (reason) {
    case 'empty':
      return 'Owner email is required.';
    case 'no_at':
      return 'Email must contain “@”.';
    case 'malformed':
      return 'Email looks malformed (e.g. ted@happytailhappydog.com).';
  }
}

// ─── Phone (optional) ────────────────────────────────────────────────────────

export type PhoneValidationError = 'too_short' | 'invalid_chars';

export type PhoneValidation =
  | { ok: true }
  | { ok: false; reason: PhoneValidationError };

/**
 * Optional field — empty input is valid. Returns ok for any plausibly-shaped
 * phone; strict E.164 normalization is the commit boundary's job.
 */
export function validateOwnerPhoneOrEmpty(raw: string): PhoneValidation {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true };
  if (trimmed.length < 7) return { ok: false, reason: 'too_short' };
  if (!PHONE_PATTERN.test(trimmed)) return { ok: false, reason: 'invalid_chars' };
  return { ok: true };
}

export function describePhoneError(reason: PhoneValidationError): string {
  switch (reason) {
    case 'too_short':
      return 'Phone is too short — include area code.';
    case 'invalid_chars':
      return 'Phone may only contain digits, spaces, dashes, parens, plus.';
  }
}

// ─── Temp password (only required when inviteMode = temp_password) ───────────

export type TempPasswordValidationError =
  | 'empty'
  | 'too_short'
  | 'no_letter'
  | 'no_digit';

export type TempPasswordValidation =
  | { ok: true }
  | { ok: false; reason: TempPasswordValidationError };

export function validateTempPassword(raw: string): TempPasswordValidation {
  if (raw.length === 0) return { ok: false, reason: 'empty' };
  if (raw.length < TEMP_PASSWORD_MIN_LENGTH) {
    return { ok: false, reason: 'too_short' };
  }
  if (!/[A-Za-z]/.test(raw)) return { ok: false, reason: 'no_letter' };
  if (!/\d/.test(raw)) return { ok: false, reason: 'no_digit' };
  return { ok: true };
}

export function describeTempPasswordError(reason: TempPasswordValidationError): string {
  switch (reason) {
    case 'empty':
      return 'Temporary password is required when not sending a magic link.';
    case 'too_short':
      return `Password must be at least ${TEMP_PASSWORD_MIN_LENGTH} characters.`;
    case 'no_letter':
      return 'Password must include a letter.';
    case 'no_digit':
      return 'Password must include a digit.';
  }
}

// ─── Draft shape ─────────────────────────────────────────────────────────────

export type OwnerDraft = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  inviteMode?: InviteMode;
  tempPassword?: string;
};

function isInviteMode(value: unknown): value is InviteMode {
  return typeof value === 'string'
    && (INVITE_MODES as readonly string[]).includes(value);
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export function readOwnerDraft(payload: Record<string, unknown>): OwnerDraft {
  const raw = payload.owner;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  return {
    email: readString(r.email),
    firstName: readString(r.firstName),
    lastName: readString(r.lastName),
    phone: readString(r.phone),
    inviteMode: isInviteMode(r.inviteMode) ? r.inviteMode : undefined,
    tempPassword: readString(r.tempPassword),
  };
}

// ─── Immutable setters ───────────────────────────────────────────────────────

export function setOwnerField<K extends keyof OwnerDraft>(
  current: OwnerDraft,
  key: K,
  value: OwnerDraft[K],
): OwnerDraft {
  return { ...current, [key]: value };
}

/**
 * Switching invite modes clears the temp password so a stale value can't
 * sneak through to the commit service when the admin flips back to magic_link.
 */
export function setInviteMode(current: OwnerDraft, mode: InviteMode): OwnerDraft {
  if (mode === 'magic_link') {
    const { tempPassword: _drop, ...rest } = current;
    return { ...rest, inviteMode: mode };
  }
  return { ...current, inviteMode: mode };
}

// ─── Step-completeness gate ──────────────────────────────────────────────────

export type OwnerCompletenessIssue =
  | 'email_missing_or_invalid'
  | 'first_name_missing'
  | 'last_name_missing'
  | 'phone_invalid'
  | 'temp_password_missing_or_invalid';

export type OwnerCompleteness =
  | { ok: true; mode: InviteMode }
  | { ok: false; issues: OwnerCompletenessIssue[] };

/**
 * Aggregate gate the wizard summary panel can use to enable/disable the
 * "Save & continue" affordance for Step 6. Mirrors the per-field validators
 * but reports as a single discriminated result so the UI doesn't re-derive
 * the rules.
 */
export function evaluateOwnerCompleteness(draft: OwnerDraft): OwnerCompleteness {
  const issues: OwnerCompletenessIssue[] = [];
  const email = validateOwnerEmail(draft.email ?? '');
  if (!email.ok) issues.push('email_missing_or_invalid');
  if (!(draft.firstName ?? '').trim()) issues.push('first_name_missing');
  if (!(draft.lastName ?? '').trim()) issues.push('last_name_missing');
  const phone = validateOwnerPhoneOrEmpty(draft.phone ?? '');
  if (!phone.ok) issues.push('phone_invalid');
  const mode: InviteMode = draft.inviteMode ?? DEFAULT_INVITE_MODE;
  if (mode === 'temp_password') {
    const pw = validateTempPassword(draft.tempPassword ?? '');
    if (!pw.ok) issues.push('temp_password_missing_or_invalid');
  }
  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, mode };
}
